import { useGetUserListById } from "@synthesis/shared-services/auth/user/hooks";
import { meta as getTuPartMetas } from "@synthesis/shared-services/files/tu-part/client";
import type { FileMetaDto } from "@synthesis/shared-services/files/tu-part/types";
import { listQueryKey, useList as useListTuParts } from "@synthesis/shared-services/internal/application/hooks";
import type { TuPartDto } from "@synthesis/shared-services/internal/application/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

export type TuPart = {
  departmentId: string;
  departmentName: string;
  fileId?: string | null;
};

export type TuPartAuthor = {
  id: string;
  name: string;
};

const formatFileName = (meta: Pick<FileMetaDto, "baseName" | "ext">): string => (meta.ext ? `${meta.baseName}.${meta.ext}` : meta.baseName);

const formatUserDtoName = (user: { id?: string | null; lastName?: string | null; firstName?: string | null; middleName?: string | null }): string => {
  const name = [user.lastName, user.firstName, user.middleName].filter(Boolean).join(" ");

  return name || "—";
};

export const useTuParts = (requestId: string) => {
  const queryClient = useQueryClient();

  const tuPartsQuery = useListTuParts({ path: { requestId } }, { query: { enabled: Boolean(requestId) } });

  const tuParts = useMemo<TuPart[]>(
    () =>
      (tuPartsQuery.data ?? []).map((item: TuPartDto) => ({
        departmentId: item.departmentId,
        departmentName: item.departmentName,
        fileId: item.fileId,
      })),
    [tuPartsQuery.data],
  );

  const fileIds = useMemo(() => tuParts.map((part) => part.fileId).filter((fileId): fileId is string => Boolean(fileId)), [tuParts]);

  const fileMetasQuery = useQuery({
    queryKey: ["tu-part-file-metas", requestId, fileIds],
    queryFn: async ({ signal }) => (await getTuPartMetas({ body: { requestId, fileIds }, signal })).data,
    enabled: Boolean(requestId) && fileIds.length > 0,
  });

  const fileMetaMap = useMemo<Map<string, FileMetaDto>>(
    () => new Map((fileMetasQuery.data ?? []).map((fileMeta) => [fileMeta.id, fileMeta])),
    [fileMetasQuery.data],
  );

  const authorIds = useMemo(
    () => Array.from(new Set((fileMetasQuery.data ?? []).map((fileMeta) => fileMeta.author).filter(Boolean))),
    [fileMetasQuery.data],
  );

  const authorsQuery = useGetUserListById(
    { query: { userIds: authorIds } },
    {
      query: { enabled: authorIds.length > 0 },
    },
  );

  const authorsMap = useMemo<Map<string, TuPartAuthor>>(() => {
    const map = new Map<string, TuPartAuthor>();
    const users = authorsQuery.data ?? [];

    for (const user of users) {
      const id = user.id;

      if (!id) {
        continue;
      }

      map.set(id, { id, name: formatUserDtoName(user) });
    }

    return map;
  }, [authorsQuery.data]);

  const invalidateTuParts = () => queryClient.invalidateQueries({ queryKey: listQueryKey({ path: { requestId } }) });

  return {
    tuParts,
    fileMetaMap,
    authorsMap,
    formatFileName,
    isLoading: tuPartsQuery.isLoading,
    isError: tuPartsQuery.isError,
    invalidateTuParts,
  };
};
